// AudioRelease — 15 audio slots; worker taps · 釋出 / · 撤回.
// Writes go directly to /audios/{code}: { released, releasedAt, releasedBy }.

import {
  Hairline,
  Han,
  HUD,
  MonoData,
  MonoLabel,
  T,
  TextBtn,
} from "@goodbye/ui";
import { audioDoc, getFirebase } from "@goodbye/firebase";
import { deleteDoc, deleteField, serverTimestamp, updateDoc } from "firebase/firestore";
import { deleteObject, getDownloadURL, listAll, ref } from "firebase/storage";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuthCtx, useAdminSyncCtx } from "../context";
import { useEventConfig } from "../hooks/useEventConfig";

function formatDuration(s: number) {
  if (!Number.isFinite(s) || s <= 0) return "—:—";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

export function AudioRelease() {
  const navigate = useNavigate();
  const { email } = useAdminAuthCtx();
  const { audios, users, listens, loading } = useAdminSyncCtx();
  const { cohortId, loading: cohortLoading } = useEventConfig();
  const released = audios.filter((a) => a.released).length;
  const next = audios.find((a) => !a.released && !a.placeholder);

  // 2026-05-30:聽過進度。只算當屆學員(cohortId 相符或缺失視為 "2025")。
  // listenedByAudio: audioId → Set(已聽的 userId)，限當屆。
  const learners = useMemo(
    () => users.filter((u) => u.role !== "worker" && (u.cohortId ?? "2025") === cohortId),
    [users, cohortId],
  );
  const cohortLearnerIds = useMemo(
    () => new Set(learners.map((u) => u.id)),
    [learners],
  );
  const listenedByAudio = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const l of listens) {
      if (!l.audioId || !l.userId) continue;
      if (!cohortLearnerIds.has(l.userId)) continue;
      const set = m.get(l.audioId) ?? new Set<string>();
      set.add(l.userId);
      m.set(l.audioId, set);
    }
    return m;
  }, [listens, cohortLearnerIds]);
  // 哪一段音檔的「誰還沒聽」名單目前展開。
  const [openListensId, setOpenListensId] = useState<string | null>(null);

  // UX pass: prevent double-tap on slow networks
  const [busy, setBusy] = useState<Set<string>>(new Set());

  function markBusy(id: string) {
    setBusy((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }
  function clearBusy(id: string) {
    setBusy((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  // 2026-05-30:工作員試聽。可聽任何有檔案的音檔(含未釋出)—— Storage 規則
  // 允許 worker 讀,released 只控學員端可見性。一次只展開一個 player:點某列
  // 「試聽」→ 解析下載 URL → 在該列下方展開 <audio controls>;再點 / 點別列
  // 收起換片。
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  async function togglePreview(id: string, storagePath: string) {
    if (previewId === id) {
      setPreviewId(null);
      setPreviewUrl(null);
      return;
    }
    setPreviewId(id);
    setPreviewUrl(null);
    setPreviewLoading(true);
    try {
      const url = await getDownloadURL(ref(getFirebase().storage, storagePath));
      // 解析期間若工作員又切到別片,丟棄這個結果。
      setPreviewId((cur) => {
        if (cur === id) setPreviewUrl(url);
        return cur;
      });
    } catch {
      setPreviewId((cur) => (cur === id ? null : cur));
    } finally {
      setPreviewLoading(false);
    }
  }

  async function release(id: string) {
    if (busy.has(id)) return;
    markBusy(id);
    try {
      await updateDoc(audioDoc(id), {
        released: true,
        releasedAt: serverTimestamp(),
        releasedBy: email ?? "unknown",
        [`cohortReleased.${cohortId}`]: true,
        [`cohortReleasedAt.${cohortId}`]: serverTimestamp(),
      });
    } finally {
      clearBusy(id);
    }
  }
  async function unrelease(id: string) {
    if (busy.has(id)) return;
    markBusy(id);
    try {
      // 60-min lock is enforced client-side (see relMs check below) instead of
      // Firestore rules because rules can't access dynamic map keys like
      // resource.data.cohortReleasedAt[cohortId]. isWorker() is still the
      // primary security gate; the lock is a UX guard against accidental taps.
      //
      // cohortReleasedAt is intentionally NOT cleared — the timestamp is needed
      // to enforce the 60-min lock on subsequent unrelease attempts.
      await updateDoc(audioDoc(id), {
        released: false,
        releasedBy: deleteField(),
        [`cohortReleased.${cohortId}`]: false,
      });
    } finally {
      clearBusy(id);
    }
  }

  // Hard-delete a worker-recorded audio. Only allowed when sourceUploadPath
  // is set — that's the marker we put on docs created via AudioNew (the
  // in-app recorder / picker). Built-in 15 slots have no sourceUploadPath
  // and stay deletion-proof.
  //
  // Cleanup three things to avoid Storage quota orphans:
  //   1. /audios/{code} Firestore doc
  //   2. audio/{code}.{ext} (transcoded mp3 / m4a, if storagePath set)
  //   3. audio-uploads/{code}-raw.* (original raw file the CF transcoded
  //      from; we list-all by prefix because the extension varies)
  async function hardDelete(id: string, storagePath: string | undefined) {
    if (busy.has(id)) return;
    markBusy(id);
    try {
      const storage = getFirebase().storage;
      // 1. Released audio (post-transcode), if any
      if (storagePath) {
        await deleteObject(ref(storage, storagePath)).catch((err) => {
          console.warn("[hardDelete] storagePath delete failed", err);
        });
      }
      // 2. Raw upload(s) — could be .webm / .m4a / .mp3 / etc. List by
      //    prefix and nuke whatever exists.
      const rawDir = ref(storage, "audio-uploads");
      const listing = await listAll(rawDir).catch(() => null);
      if (listing) {
        const matchPrefix = `${id}-raw`;
        const matches = listing.items.filter((i) => i.name.startsWith(matchPrefix));
        await Promise.allSettled(matches.map((i) => deleteObject(i)));
      }
      // 3. Firestore doc — last, so we don't strand the data if Storage
      //    delete throws (worker can retry).
      await deleteDoc(audioDoc(id));
    } catch (err) {
      console.error("[hardDelete] failed", err);
    } finally {
      clearBusy(id);
      setConfirmDelete((prev) => {
        if (prev === id) return null;
        return prev;
      });
    }
  }

  // Two-tap confirm so a single accidental tap doesn't nuke a recording.
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  // 釋出 / 撤回 也加二段確認(使用者要兩個都加):key = "release:{id}" 或
  // "unrelease:{id}",第一次 arm、第二次才執行。4s auto-revert。2026-05-30。
  const [confirmAction, setConfirmAction] = useState<string | null>(null);
  useEffect(() => {
    if (!confirmAction) return;
    const t = window.setTimeout(() => setConfirmAction(null), 4000);
    return () => window.clearTimeout(t);
  }, [confirmAction]);
  // 2026-05-30: delete now also has an explicit 取消 button + 4s auto-revert,
  // matching release/unrelease (all three behave the same in confirm state).
  useEffect(() => {
    if (!confirmDelete) return;
    const t = window.setTimeout(() => setConfirmDelete(null), 4000);
    return () => window.clearTimeout(t);
  }, [confirmDelete]);

  // Wait for cohortId to load before rendering release actions — a wrong-cohort
  // default of "2025" during a 2026 event could silently release to the wrong cohort.
  if (cohortLoading)
    return (
      <div style={{ minHeight: "100dvh", display: "flex", alignItems: "center", justifyContent: "center", background: T.paper }}>
        <Han size={13} color={T.ink350}>載 入 屆 別 設 定 中…</Han>
      </div>
    );

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        background: T.paper,
        paddingBottom: "calc(140px + env(safe-area-inset-bottom, 0px))",
      }}
    >
      <HUD label="工 作 端" />
      <div
        style={{
          padding: "20px 32px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <MonoLabel size={12}>{email}</MonoLabel>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
          }}
        >
          <Han size={22} color={T.ink900}>音檔列表</Han>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
            <MonoData size={12} color={T.ink500}>
              {released} / {audios.length} 已釋出
            </MonoData>
            <TextBtn
              size={12}
              color={T.ink900}
              framed={false}
              onClick={() => navigate("/audio/new")}
              style={{ padding: "4px 6px" }}
            >
              · 新 增
            </TextBtn>
          </div>
        </div>
        {next && (
          <Han size={13} color={T.ink350}>
            下一段:{next.code} {next.title}
          </Han>
        )}
      </div>
      <Hairline style={{ margin: "8px 32px 0" }} />

      <div style={{ padding: "0 32px" }}>
        {loading && <Han size={13} color={T.ink350}>連線中…</Han>}
        {audios.map((a) => {
          // 2026-05-30: a row is "in confirm" if any of its three actions is
          // armed (release / unrelease share confirmAction; delete uses
          // confirmDelete). In that state we swap the whole action area for
          // [confirm][cancel] and hide 試聽 — see the action row below.
          const confirmingThisRow =
            confirmAction === `release:${a.id}` ||
            confirmAction === `unrelease:${a.id}` ||
            confirmDelete === a.id;
          return (
          <Fragment key={a.id}>
          <div
            style={{
              // Day 13 視覺強化:relative + 左側絕對定位 bar
              position: "relative",
              display: "grid",
              gridTemplateColumns: "34px 1fr auto",
              alignItems: "center",
              gap: 12,
              padding: "14px 12px 14px 16px",
              marginLeft: -12,
              marginRight: -12,
              borderBottom: `1px solid ${T.ink100}`,
              background: a.released ? T.paperSink : "transparent",
              opacity: a.placeholder ? 0.55 : 1,
            }}
          >
            {/* 左側狀態棒: released = 4px 滿格 ink900;unreleased =
                4px ink-350(B4 提到更搶眼,跟 released 同寬不同色,
                工作員快速掃 list 一眼看出哪些還沒釋出);placeholder
                不顯示。 */}
            {!a.placeholder && (
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 4,
                  background: a.released ? T.ink900 : T.ink350,
                }}
              />
            )}
            <MonoData
              size={14}
              color={a.released ? T.ink900 : T.ink350}
            >
              {a.code}
            </MonoData>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Han
                size={15}
                color={a.released ? T.ink900 : T.ink500}
                lh={1.5}
              >
                {a.title}
              </Han>
              {/* B4: 未釋出狀態 mono label — 工作員一眼看到「這條還
                  沒釋出」、不會跟「轉檔中」「尚未錄製」placeholder 狀態
                  混淆。released 跟 placeholder 都不顯示此 label。 */}
              {!a.released && !a.placeholder && (
                <MonoLabel size={11} color={T.ink700}>
                  · 未 釋 出
                </MonoLabel>
              )}
              <Han size={12} color={T.ink350}>
                {a.place || (a.sourceUploadPath ? "(現場錄音)" : "—")} ·{" "}
                {formatDuration(a.duration)}
                {a.placeholder && !a.sourceUploadPath && " · 尚未錄製"}
                {a.placeholder && a.sourceUploadPath && " · 轉檔中…"}
                {a.transcodeError && ` · 錯誤: ${a.transcodeError}`}
              </Han>
              {/* 2026-05-30:聽過進度。只在已釋出音檔顯示(未釋出學員看不到、
                  不可能聽)。點一下展開「還沒聽」名單。 */}
              {a.released && learners.length > 0 && (() => {
                const heard = listenedByAudio.get(a.id) ?? new Set<string>();
                const heardCount = learners.filter((u) => heard.has(u.id)).length;
                const allHeard = heardCount === learners.length;
                return (
                  <button
                    type="button"
                    onClick={() =>
                      setOpenListensId((cur) => (cur === a.id ? null : a.id))
                    }
                    style={{
                      alignSelf: "flex-start",
                      background: "transparent",
                      border: "none",
                      padding: "2px 0",
                      margin: 0,
                      cursor: "pointer",
                      fontFamily: T.mono,
                      fontSize: 11,
                      letterSpacing: "0.04em",
                      color: allHeard ? T.ink500 : T.ink900,
                      touchAction: "manipulation",
                      // 2026-05-30:強制一行,不再被擠成兩行。文案也縮短
                      // (看誰還沒聽 → 誰沒聽、去掉中間多餘的「· 」分隔)。
                      whiteSpace: "nowrap",
                    }}
                  >
                    · 聽過 {heardCount}/{learners.length}
                    {!allHeard && (openListensId === a.id ? " 收起" : " 誰沒聽 ▾")}
                  </button>
                );
              })()}
            </div>
            {/* Stage 7: wrap action buttons in a flex container so
                they share the grid's third (auto) column instead of
                fighting for it as separate grid items. Without this,
                a row with both「釋出」and「刪除」squeezes both into
                one narrow track and the 0.2em letterSpacing breaks
                the 中文 into a vertical column. nowrap + tighter ls
                belt-and-braces the same issue. */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: confirmingThisRow ? 12 : 8,
                flexShrink: 0,
              }}
            >
              {confirmingThisRow ? (
                // 2026-05-30: confirm state — replace the whole action area
                // with [確定XX?] [取消]. 試聽 is hidden (no need to preview
                // mid-confirm, and dropping it lets two 中文 buttons fit the
                // grid's auto column without the squeeze noted above). Confirm
                // button = ink900 + heavy border (primary); 取消 = light
                // secondary (ink500 / ink200, framed=false) matching the
                // _PinEditPanel · 取 消 pattern. Tapping 確定 clears state AND
                // fires, so the row leaves confirm state immediately and shows
                // the now-busy primary button; cancel only exists in the
                // armed-but-not-fired window.
                (() => {
                  const isUnrelease = confirmAction === `unrelease:${a.id}`;
                  const isDelete = confirmDelete === a.id;
                  const confirmText = busy.has(a.id)
                    ? isDelete
                      ? "刪 除 中…"
                      : isUnrelease
                        ? "收 回 中…"
                        : "釋 出 中…"
                    : isDelete
                      ? "確 定 刪 除?"
                      : isUnrelease
                        ? "確 定 撤 回?"
                        : "確 定 釋 出?";
                  const onConfirm = () => {
                    if (busy.has(a.id)) return;
                    if (isDelete) {
                      setConfirmDelete(null);
                      hardDelete(a.id, a.storagePath);
                    } else if (isUnrelease) {
                      setConfirmAction(null);
                      unrelease(a.id);
                    } else {
                      setConfirmAction(null);
                      release(a.id);
                    }
                  };
                  const onCancel = () => {
                    if (isDelete) setConfirmDelete(null);
                    else setConfirmAction(null);
                  };
                  return (
                    <>
                      <TextBtn
                        size={12}
                        color={T.ink900}
                        ls="0.12em"
                        framed
                        onClick={onConfirm}
                        disabled={busy.has(a.id)}
                        style={{
                          padding: "8px 12px",
                          border: `1px solid ${T.ink900}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {confirmText}
                      </TextBtn>
                      <TextBtn
                        size={12}
                        color={T.ink500}
                        ls="0.12em"
                        framed={false}
                        onClick={onCancel}
                        disabled={busy.has(a.id)}
                        style={{
                          padding: "8px 12px",
                          border: `1px solid ${T.ink200}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        取 消
                      </TextBtn>
                    </>
                  );
                })()
              ) : (
                <>
                  {/* 2026-05-30:試聽鈕。有檔案(非 placeholder)就能聽,含未釋出。 */}
                  {!a.placeholder && a.storagePath && (
                    <TextBtn
                      size={12}
                      color={previewId === a.id ? T.ink900 : T.ink500}
                      ls="0.12em"
                      framed
                      onClick={() => togglePreview(a.id, a.storagePath)}
                      style={{
                        padding: "8px 12px",
                        border: `1px solid ${previewId === a.id ? T.ink900 : T.ink200}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {previewId === a.id
                        ? previewLoading
                          ? "載 入 中…"
                          : "收 起"
                        : "試 聽"}
                    </TextBtn>
                  )}
                  {a.placeholder ? (
                    <MonoLabel size={11} color={T.ink350}>
                      {a.sourceUploadPath ? "· 轉 檔 中" : "· 未 上 傳"}
                    </MonoLabel>
                  ) : a.released ? (
                    (() => {
                      // 2026-05-30:已釋出超過 60 分鐘 → 不能撤回。
                      // 讀 cohort-specific timestamp 優先，無則 fallback 全域 releasedAt。
                      // 注意:60分鎖從 Firestore rules 移到 client-side，因為 rules
                      // 無法動態存取 cohortReleasedAt[cohortId] map key。
                      const relMs = (a.cohortReleasedAt?.[cohortId] ?? a.releasedAt)?.toMillis?.() ?? 0;
                      const tooLate = relMs > 0 && Date.now() - relMs > 60 * 60 * 1000;
                      if (tooLate) {
                        return (
                          <MonoLabel size={11} color={T.ink350}>
                            · 超 過 60 分 · 不 可 撤 回
                          </MonoLabel>
                        );
                      }
                      return (
                        <TextBtn
                          size={12}
                          color={T.ink350}
                          ls="0.12em"
                          framed={false}
                          onClick={() => {
                            if (busy.has(a.id)) return;
                            setConfirmAction(`unrelease:${a.id}`);
                          }}
                          disabled={busy.has(a.id)}
                          style={{
                            padding: "8px 12px",
                            border: `1px solid ${T.ink200}`,
                            whiteSpace: "nowrap",
                          }}
                        >
                          撤 回
                        </TextBtn>
                      );
                    })()
                  ) : (
                    <TextBtn
                      size={13}
                      color={T.ink900}
                      ls="0.12em"
                      framed={false}
                      onClick={() => {
                        if (busy.has(a.id)) return;
                        setConfirmAction(`release:${a.id}`);
                      }}
                      disabled={busy.has(a.id)}
                      style={{
                        padding: "8px 14px",
                        border: `1px solid ${T.ink200}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      釋 出
                    </TextBtn>
                  )}
                  {/* Worker-recorded audios (sourceUploadPath set) are deletable.
                      Built-in 15 segments have no sourceUploadPath and stay
                      permanent. First tap arms confirm (handled by the
                      confirmingThisRow branch above). Still pure greyscale. */}
                  {a.sourceUploadPath && (
                    <TextBtn
                      size={12}
                      color={T.ink500}
                      ls="0.12em"
                      framed={false}
                      onClick={() => setConfirmDelete(a.id)}
                      disabled={busy.has(a.id)}
                      style={{
                        padding: "8px 12px",
                        border: `1px solid ${T.ink200}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      刪 除
                    </TextBtn>
                  )}
                </>
              )}
            </div>
          </div>
          {/* 2026-05-30:試聽展開 — 原生 <audio controls> 在該列下方。autoPlay
              讓點「試聽」後直接開始播。URL 解析中先不渲染。 */}
          {previewId === a.id && previewUrl && (
            <div
              style={{
                padding: "0 12px 14px 16px",
                marginLeft: -12,
                marginRight: -12,
                borderBottom: `1px solid ${T.ink100}`,
                background: a.released ? T.paperSink : "transparent",
              }}
            >
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <audio
                controls
                autoPlay
                src={previewUrl}
                style={{ width: "100%", height: 36 }}
              />
            </div>
          )}
          {/* 2026-05-30:「還沒聽」名單 — 點上方「看誰還沒聽」展開。 */}
          {openListensId === a.id && (() => {
            const heard = listenedByAudio.get(a.id) ?? new Set<string>();
            const notHeard = learners.filter((u) => !heard.has(u.id));
            return (
              <div
                style={{
                  padding: "10px 12px 14px 16px",
                  marginLeft: -12,
                  marginRight: -12,
                  borderBottom: `1px solid ${T.ink100}`,
                  background: T.paperSink,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <MonoLabel size={11} color={T.ink500}>
                  · 還 沒 聽 ({notHeard.length})
                </MonoLabel>
                {notHeard.length === 0 ? (
                  <Han size={12} color={T.ink500}>· 全 部 學 員 都 聽 過 了</Han>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {notHeard.map((u) => (
                      <span
                        key={u.id}
                        style={{
                          fontFamily: T.han,
                          fontSize: 13,
                          color: T.ink900,
                          border: `1px solid ${T.ink200}`,
                          padding: "3px 8px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {u.no} {u.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
          </Fragment>
          );
        })}
      </div>
    </div>
  );
}
